// src/features/jobs/utils/materialSummary.js

function round3(n) {
  return Math.round((Number(n) || 0) * 1000) / 1000;
}

function isPVC(m) {
  return (m.material || '').toUpperCase() === 'PVC';
}

function isCopper(m) {
  return (m.material || '').toUpperCase() === 'COPPER';
}

function isBLR(m) {
  const mat = (m.material || '').toUpperCase();
  return mat === 'BLR' || mat === 'STEEL'; // STEEL = legacy alias
}

/**
 * Construiește un label “uman” pentru fiecare item.
 * - PVC: "PVC NATUR (BLUE)" sau "PVC YM 2 - exterior_sheat"
 * - COPPER: "COPPER STRANDED Ø0.242" etc.
 */
export function materialLabel(item) {
  if (!item) return 'UNKNOWN';

  if (isPVC(item)) {
    const grade = item.grade ? String(item.grade) : 'PVC';
    const color = item.color ? ` (${item.color})` : '';
    const layer = item.layer ? ` - ${item.layer}` : '';
    return `PVC ${grade}${color}${layer}`;
  }

  if (isCopper(item)) {
    const wireType = item.wireType ? String(item.wireType) : 'WIRE';
    const d = item.wireDiameter != null ? ` Ø${item.wireDiameter}` : '';
    return `COPPER ${wireType}${d}`;
  }

  if (isBLR(item)) {
    // grade carries the spec ("15 x 0.15") and survives the execution pipeline,
    // where wide/thickness are dropped.
    if (item.grade) return `BLR ${item.grade}`;
    return item.wide && item.thickness
      ? `BLR ${item.wide} x ${item.thickness}`
      : 'BLR armouring';
  }

  // fallback generic
  const material = item.material ? String(item.material) : 'MATERIAL';
  const form = item.form ? ` ${item.form}` : '';
  const grade = item.grade ? ` ${item.grade}` : '';
  return `${material}${form}${grade}`.trim();
}

/**
 * Primește materialPlanSummary sau materialExecutionSummary:
 * [
 *   { material, grade, color, planned_kg }
 * ]
 * și returnează:
 * - totalKg
 * - stats: [{ type, valueKg }]
 * - chartData: [{ name, value }]
 */
export function buildMaterialStats(summary = [], opts = {}) {
  const {
    topN = 6, // câte slice-uri maxime în pie
    minSliceKg = 0 // poți seta 0.2 ca să nu-ți umple chart-ul cu zgomot
  } = opts;

  // 1) group by label
  const map = new Map();
  for (const item of summary || []) {
    const kg = Number(item.planned_kg ?? item.executed_kg ?? item.kg ?? 0);
    if (!kg || kg <= 0) continue;

    const label = materialLabel(item);

    map.set(label, (map.get(label) || 0) + kg);
  }

  // 2) make array
  let stats = Array.from(map.entries()).map(([type, kg]) => ({
    type,
    valueKg: round3(kg)
  }));

  // 3) filter tiny
  if (minSliceKg > 0) {
    stats = stats.filter(s => s.valueKg >= minSliceKg);
  }

  // 4) sort desc
  stats.sort((a, b) => b.valueKg - a.valueKg);

  const totalKg = round3(stats.reduce((sum, s) => sum + s.valueKg, 0));

  // 5) top N + Other
  let chartData = stats.map(s => ({ name: s.type, value: s.valueKg }));

  if (chartData.length > topN) {
    const top = chartData.slice(0, topN);
    const rest = chartData.slice(topN);
    const otherValue = round3(rest.reduce((sum, x) => sum + x.value, 0));
    chartData =
      otherValue > 0 ? [...top, { name: 'Other', value: otherValue }] : top;
  }

  return { totalKg, stats, chartData };
}
